import * as React from "react";
import { useState } from "react";
import { ChevronDown, ChevronUp, MapPin } from "lucide-react";
import { StatusBadge } from "./column-formatter";
import { Pagination } from "./pagination";

// Memoized single card component
const MobileCard = React.memo(
  ({ row, rowIndex, rowKey, mobileConfig, columns, callbacks, excludedColumns }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleCard = () => setIsExpanded((prev) => !prev);

    const loanId = row[mobileConfig.loanId];
    const loanType = row[mobileConfig.loanType];
    const expandedColumns = columns.filter(
      (col) => !mobileConfig.excludedColumns?.includes(col.key)
    );

    const renderValue = (column) =>
      column?.renderCell
        ? column.renderCell(row, rowIndex, callbacks)
        : row[column.key] || "-";

    const statusColumn = columns.find((col) => col.key === "status");
    const addressColumn = columns.find((col) => col.key === "propertyAddress");
    const actionsColumn = columns.find((col) => col.key === "actions");

    const getColumnValue = (column) => {
      if (!column) return null;
      return column.renderCell
        ? column.renderCell(row, rowIndex, callbacks)
        : row[column.key] ?? "-";
    };

    const statusValue = getColumnValue(statusColumn);
    const addressValue = getColumnValue(addressColumn);

    return (
      <div
        key={row[rowKey] || rowIndex}
        className="bg-neutral-1 rounded-2xl border border-neutral-5 overflow-hidden shadow-sm"
      >
        {/* Card Header */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={loanId} variant="yin" size="sm" />
              {loanType && (
                <StatusBadge status={loanType} variant="yang" size="sm" />
              )}
            </div>
            {statusValue && (
              <span
              className={`px-2 py-1 text-sm font-medium rounded-lg whitespace-nowrap`}
              >
                {statusValue}
              </span>
            )}
          </div>

          {/* Address */}
          {addressValue && (
            <div className="flex items-start gap-2 mb-4 py-2">
              <MapPin className="w-5 h-5 text-neutral-8 mt-0.5 flex-shrink-0" />
              <span className="text-neutral-10 text-sm font-medium">
                {addressValue}
              </span>
            </div>
          )}

          {/* Toggle and Action Buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={toggleCard}
              className="flex items-center gap-2 text-primary-3 text-sm font-medium hover:text-primary-2 transition-colors"
            >
              {isExpanded ? (
                <>
                  Show Less <ChevronUp className="w-5 h-5" />
                </>
              ) : (
                <>
                  Show More <ChevronDown className="w-5 h-5" />
                </>
              )}
            </button>
            {actionsColumn && actionsColumn.renderCell && (
              <div className="flex items-center gap-2">
                {actionsColumn.renderCell(row, rowIndex, callbacks)}
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content */}
        <div
          className={`
            overflow-hidden transition-[max-height]
            ease-in-out
            ${
              isExpanded ? "max-h-[700px] duration-500" : "max-h-0 duration-200"
            }
          `}
        >
          <div className="px-5 py-5 border-t border-neutral-4">
            <div className="space-y-4">
              {expandedColumns.map((column) => {
                return (
                  <div
                    key={column.key}
                    className="flex justify-between items-center gap-4"
                  >
                    <span className="text-neutral-7 text-sm">
                      {column.label}
                    </span>
                    <span className="text-neutral-10 text-sm text-right">
                      {renderValue(column)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const MobileTableCard = ({
  columns = [],
  data = [],
  rowKey = "id",
  className = "",
  callbacks = {},
  mobileConfig,
  excludedColumns = [],
  totalLoanCount = 0,
  currentPage = 1,
  pageSize = 5,
  handlePageChange,
}) => {
  const totalPages = Math.ceil(totalLoanCount / pageSize);
  const shouldShowPagination = totalLoanCount > pageSize;

  return (
    <div className={`w-full ${className}`}>
      <div className="space-y-4">
        {data.map((row, rowIndex) => (
          <MobileCard
            key={row[rowKey] || rowIndex}
            row={row}
            rowIndex={rowIndex}
            rowKey={rowKey}
            mobileConfig={mobileConfig}
            columns={columns}
            callbacks={callbacks}
            excludedColumns={excludedColumns}
          />
        ))}
      </div>

      {/* Pagination */}
      {shouldShowPagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};
export default MobileTableCard;
export { MobileTableCard };
